// The one thing a phone screen floats over its own content: the way to make a
// new one of whatever is being read.
//
// Not the header's button narrowed, and not `AddAction`'s circle moved. Those
// two live at the top of a screen, which is where a pointer looks and where a
// thumb cannot reach — and a screen read by scrolling puts its top out of
// sight by the second swipe. A list of eight payments with Request at the top
// is a button somebody has to scroll back up to. Floating, it is where the
// thumb already is, over the part of the screen the eye is not reading.
//
// It carries its label rather than being a bare plus. A disc in a corner with
// nothing beside it to read it against says nothing about what pressing it
// does — the same reason `AddAction` keeps its words on a desktop, one width
// down. The label is what makes this a pill and not a riddle.
//
// Phone only, by the caller: every screen that has one of these has width for
// a labelled button in its own furniture from `md` up, and the two together
// would be the same action twice.

import React from "react";
import { PlusIcon } from "./Icons";

export interface FloatingActionProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * What pressing it does, written out — "New task", "New payment". A sentence
   * the screen does not already say, since this pill can be the only thing on
   * screen once a list has scrolled.
   */
  label: string;
  /**
   * The mark beside the label. A plus unless the action is not a new anything.
   */
  icon?: React.ReactNode;
}

/**
 * A frosted pill at the bottom right, floating clear of the tab bar.
 *
 * `--tab-bar-room` is the bar's height where the bar is drawn and zero on a
 * screen that has claimed the bottom edge for itself — see `useHiddenTabBar` —
 * so this one measure puts the pill above the bar on an ordinary screen and
 * off the bottom edge on those. It floats by `--tab-bar-pad-bottom` either
 * way, which is the home bar's inset where there is one and a flat 1.25rem
 * where there is not. Taking the shell's own numbers rather than a gap chosen
 * per screen is what keeps every pill in the app at one height.
 *
 * Frosted, the way the tab bar is: the page scrolls under this, and a flat
 * white lozenge over a white list reads as a card that has come loose rather
 * than as a layer above it. The same three parts the bar uses and for the same
 * reasons — the blur is what the page becomes, the saturation puts back what
 * blurring averaged away, and the 72% fill is what the teal label stands on.
 * The hairline turns white with it, because on glass an edge is light caught
 * along the rim and not a border drawn round a box, and the drop shadow stays
 * since saying how far off the page it floats is the one thing glass cannot do
 * for itself. Solid white wherever `backdrop-filter` is not supported, because
 * a 72% pill with nothing blurred behind it is just the list showing through
 * the button.
 *
 * Marked disabled to a reader rather than natively disabled, so the press that
 * did not know why is still answered — spread `lockedProps` onto it and the
 * `aria-disabled` it sets is what dims this. See `useSubscriptionGate`.
 */
const FloatingAction: React.FC<FloatingActionProps> = ({
  label,
  icon,
  className = "",
  style,
  ...rest
}) => {
  const locked = rest["aria-disabled"] === true || rest["aria-disabled"] === "true";

  return (
    <button
      type="button"
      // A caller's own style is spread over this rather than under it, so one
      // that needs to sit somewhere else can say so without losing the rest.
      style={{
        bottom: "calc(var(--tab-bar-room, 0px) + var(--tab-bar-pad-bottom))",
        ...style,
      }}
      className={`fixed right-page z-20 inline-flex items-center gap-2 rounded-full border border-white/70 bg-white px-5 py-4 text-[15px]/[22px] font-medium text-[#00424B] shadow-[0_8px_28px_rgba(0,66,75,0.22)] transition-transform duration-[320ms] ease-[cubic-bezier(0.34,1.56,0.64,1)] touch-manipulation [-webkit-tap-highlight-color:transparent] supports-[backdrop-filter]:bg-white/[0.72] supports-[backdrop-filter]:backdrop-blur-xl supports-[backdrop-filter]:backdrop-saturate-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2CB]/40 motion-safe:active:scale-[0.95] motion-safe:active:duration-100 motion-safe:active:ease-out ${
        locked ? "opacity-60" : ""
      } ${className}`}
      {...rest}
    >
      {icon ?? <PlusIcon className="h-5 w-5 flex-shrink-0" />}
      {label}
    </button>
  );
};

export default FloatingAction;
